import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";

export interface Company {
  id: string;
  name: string;
  createdAt: string;
  isDefault?: boolean;
  role?: string;
}

export interface CreateCompanyData {
  name: string;
}

interface CompaniesResponse {
  companies: Company[];
}

interface CompanyResponse {
  company: Company;
}

interface SetDefaultResponse {
  success: boolean;
  company: Company;
}

interface LeaveResponse {
  success: boolean;
}

// Get user's companies
export function useCompanies() {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["companies"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<CompaniesResponse>("/api/companies", {
        headers,
      });
    },
    enabled: auth.isSignedIn,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Create a company
export function useCreateCompany() {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCompanyData) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<CompanyResponse>("/api/companies", {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

// Set default company
export function useSetDefaultCompany() {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<SetDefaultResponse>(`/api/companies/${id}/default`, {
        method: "PATCH",
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

// Leave a company
export function useLeaveCompany() {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<LeaveResponse>(`/api/companies/${id}/leave`, {
        method: "DELETE",
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}
